// Native MP4 seeking with the reference film's 0.08/60 Hz easing.
// Sticky geometry works with both Horizon's desktop scroller and mobile document.
class AyumeScrollFilm extends HTMLElement {
  connectedCallback() {
    if (this.abort) return;
    this.abort = new AbortController();
    this.video = this.querySelector('video');
    this.stage = this.querySelector('.av-film-stage');
    this.button = this.querySelector('[data-toggle-motion]');
    this.motion = matchMedia('(prefers-reduced-motion:reduce)');
    this.portrait = matchMedia('(max-width:989px) and (orientation:portrait)');
    this.touch = matchMedia('(hover:none) and (pointer:coarse)');
    const signal = this.abort.signal;
    this.motion.addEventListener('change', () => this.reset(), {signal});
    this.portrait.addEventListener('change', () => this.reset(), {signal});
    this.button?.addEventListener('click', () => {
      this.userStill = !this.userStill; this.reset();
    }, {signal});
    document.addEventListener('pointerdown', () => this.prime(), {passive:true, signal});
    document.addEventListener('touchstart', () => this.prime(), {passive:true, signal});
    document.addEventListener('visibilitychange', () => document.hidden ? this.suspend() : this.resume(), {signal});
    window.addEventListener('pagehide', () => this.suspend(), {signal});
    window.addEventListener('pageshow', () => this.resume(), {signal});
    this.near = new IntersectionObserver(entries => {
      if (entries[0].isIntersecting) this.load();
    }, {rootMargin:'300px'});
    this.view = new IntersectionObserver(entries => {
      const entering = entries[0].isIntersecting && !this.visible;
      this.visible = entries[0].isIntersecting;
      if (entering) this.resume();
      else if (!this.visible) this.suspend();
    });
    this.view.observe(this);
    this.reset();
  }
  reset() {
    this.stop();
    this.failed = false; this.recovered = false; this.elapsed = 0;
    this.still = this.motion.matches || navigator.connection?.saveData || this.userStill;
    this.src = this.portrait.matches ? this.dataset.mobile : this.dataset.desktop;
    this.button?.toggleAttribute('hidden', this.motion.matches || navigator.connection?.saveData || !this.src);
    if (this.button) this.button.textContent = this.userStill ? this.dataset.motionLabel : this.dataset.stillLabel;
    this.classList.toggle('is-scrubbing', Boolean(this.src && !this.still));
    if (!this.still && this.src) this.near.observe(this);
  }
  load() {
    if (this.loaded || this.failed || this.still || !this.src) return;
    // Let the poster win the first mobile paint and network priority.
    const poster = this.querySelector('img');
    if (poster && !poster.complete) {
      if (!this.waitingPoster) {
        this.waitingPoster = true;
        const next = () => { this.waitingPoster = false; this.load(); };
        poster.addEventListener('load', next, {once:true, signal:this.abort.signal});
        poster.addEventListener('error', next, {once:true, signal:this.abort.signal});
      }
      return;
    }
    this.loaded = true; this.near.unobserve(this);
    const video = this.video, signal = this.abort.signal;
    if (!this.mediaBound) {
      this.mediaBound = true;
      video.addEventListener('error', () => {
        if (this.loaded) this.fallback('media-error');
      }, {signal});
      video.addEventListener('loadeddata', () => {
        if (!this.loaded || this.failed) return;
        if (!Number.isFinite(video.duration) || video.duration <= 0) return this.fallback('duration');
        this.ready = true; this.elapsed = 0; this.seekStarted = 0;
        this.smoothed = this.target();
        this.prime();
        this.queue();
      }, {signal});
      video.addEventListener('seeked', () => {
        this.seekStarted = 0;
        if (this.visible && !document.hidden && !this.failed && !this.still && video.readyState >= 2) this.classList.add('is-ready');
        this.queue();
      }, {signal});
    }
    video.muted = true; video.playsInline = true; video.preload = 'auto';
    video.src = this.src; video.load(); this.queue();
  }
  prime() {
    if (!this.visible || !this.loaded || !this.ready || this.primed || this.priming || this.failed || this.still || document.hidden) return;
    this.priming = true;
    this.primingStarted = performance.now();
    // Called directly within touch/pointer events too: Safari can unlock decoding.
    const attempt = this.video.play();
    this.primeAttempt = attempt;
    attempt?.then(() => {
      if (this.primeAttempt !== attempt) return;
      this.video.pause(); this.primed = true; this.priming = false; this.queue();
    }).catch(() => {
      if (this.primeAttempt !== attempt) return;
      this.priming = false;
      // Paused seeks can still work. A later real gesture retries the unlock.
      this.video.pause(); this.queue();
    });
  }
  target() {
    const travel = Math.max(1, this.offsetHeight - this.stage.offsetHeight);
    const progress = Math.max(0, Math.min(1, -this.getBoundingClientRect().top / travel));
    return progress * Math.max(0, this.video.duration - 1 / 24);
  }
  // Scroll time -> one real frame of the file. Seeking only when that frame
  // changes avoids re-decoding the same image; frames listed in data-skip-*
  // (exact repeats inside the delivered films) are stepped over so the film
  // never holds one image twice as long as its neighbours.
  frameAt(time) {
    const fps = 24, count = Math.max(1, Math.round(this.video.duration * fps));
    const skip = (this.portrait.matches ? this.dataset.skipMobile : this.dataset.skipDesktop) || '';
    if (!this.frames || this.frames.count !== count || this.frames.skip !== skip) {
      const repeated = new Set(skip.split(',').filter(Boolean).map(Number).filter(Number.isInteger));
      const list = [];
      for (let i = 0; i < count; i++) if (!repeated.has(i)) list.push(i);
      this.frames = {count, skip, list};
    }
    const {list} = this.frames;
    const progress = Math.max(0, Math.min(1, time / Math.max(1 / fps, (count - 1) / fps)));
    return list[Math.min(list.length - 1, Math.floor(progress * (list.length - 1) + 1e-6))];
  }
  queue() {
    if (this.frame || !this.visible || document.hidden || !this.loaded || this.failed || this.still) return;
    this.frame = requestAnimationFrame(now => this.tick(now));
  }
  tick(now) {
    this.frame = 0;
    if (!this.visible || document.hidden || this.failed || this.still) return;
    const dt = this.lastTick ? Math.min(64, now - this.lastTick) : 1000 / 60;
    this.lastTick = now;
    const video = this.video;
    if (!this.ready || video.readyState < 2) {
      this.elapsed += dt;
      this.classList.remove('is-ready');
      if (this.elapsed > (this.ready ? 3000 : 15000)) return this.recover('decoder-unavailable');
    } else if (!video.seekable.length || video.seekable.end(video.seekable.length - 1) < .04) {
      // A cold CDN response can decode frame zero yet expose no seek range.
      // Keep the poster and reload once; do not mistake seeked-at-zero for success.
      this.classList.remove('is-ready');
      this.elapsed += dt;
      if (this.elapsed > 2000) return this.recover('empty-seek-range');
    } else if (this.priming && now - this.primingStarted > 3000) {
      return this.recover('play-timeout');
    } else if (!this.priming) {
      this.elapsed = 0;
      const target = this.target();
      this.smoothed ??= target;
      this.smoothed += (target - this.smoothed) * (1 - Math.pow(.92, dt / (1000 / 60)));
      if (Math.abs(target - this.smoothed) < .002) this.smoothed = target;
      const frame = this.frameAt(this.smoothed), time = (frame + .5) / 24;
      if (Math.abs(video.currentTime - (this.lastPresentedTime ?? 0)) > .02 || Math.abs(video.currentTime - time) < .12) {
        this.noProgress = 0; this.lastPresentedTime = video.currentTime;
      } else {
        this.noProgress = (this.noProgress || 0) + dt;
        if (this.noProgress > 3500) return this.recover('no-progress');
      }
      if (video.seeking) {
        this.seekStarted ||= now;
        if (now - this.seekStarted > 3000) return this.recover('seek-timeout');
      } else if (Math.floor(video.currentTime * 24 + 1e-6) !== frame) {
        this.seekStarted = now;
        try { video.currentTime = time; } catch { return this.recover('seek-error'); }
      } else {
        this.seekStarted = 0; this.classList.add('is-ready');
      }
    }
    this.queue();
  }
  suspend() {
    cancelAnimationFrame(this.frame); this.frame = 0;
    this.lastTick = 0; this.seekStarted = 0; this.elapsed = 0;
    this.noProgress = 0;
    this.primeAttempt = null; this.priming = false;
    this.primed = false;
    this.video?.pause();
  }
  resume() {
    if (document.hidden || this.still || !this.src) return;
    this.suspend(); this.classList.remove('is-ready');
    // A decoder failure belongs to the current visit to this film, never to
    // the whole page. Re-entering or returning from BFCache starts a new visit.
    this.failed = false; this.recovered = false; this.lastPresentedTime = null;
    this.primed = false; this.priming = false;
    if (!this.loaded) {
      this.near.observe(this);
      if (this.visible) this.load();
      return;
    }
    if (this.video.readyState === 0 || !this.video.hasAttribute('src')) {
      this.ready = false; this.video.src = this.src; this.video.load();
    }
    if (this.ready) this.smoothed = this.target();
    this.prime();
    this.queue();
  }
  recover(reason) {
    this.classList.remove('is-ready'); this.suspend();
    if (this.recovered) return this.fallback(reason);
    this.recovered = true; this.ready = false; this.primed = false; this.priming = false;
    this.video.load(); this.queue();
  }
  fallback(reason) {
    this.failureReason = reason;
    this.failed = true; this.stop(false);
    // Never collapse the reserved track after paint, including while approaching
    // the film. A failed decoder must not move the section that follows it.
    if (this.button) this.button.hidden = true;
  }
  stop(removeGeometry = true) {
    this.near?.unobserve(this); this.suspend();
    this.classList.remove('is-ready');
    if (removeGeometry) this.classList.remove('is-scrubbing');
    this.loaded = false; this.ready = false; this.smoothed = null;
    this.primed = false; this.priming = false;
    if (this.video?.hasAttribute('src')) { this.video.removeAttribute('src'); this.video.load(); }
  }
  disconnectedCallback() {
    this.stop(); this.abort?.abort(); this.near?.disconnect(); this.view?.disconnect();
    this.abort = null; this.mediaBound = false;
  }
}
if (!customElements.get('ayume-scroll-film')) customElements.define('ayume-scroll-film', AyumeScrollFilm);
